import type { MilkdownPlugin } from '@milkdown/kit/ctx';
import { parserCtx, prosePluginsCtx, schemaCtx, SchemaReady } from '@milkdown/kit/core';
import { isTextOnlySlice } from '@milkdown/kit/prose';
import { DOMParser, DOMSerializer } from '@milkdown/kit/prose/model';
import { Plugin, PluginKey } from '@milkdown/kit/prose/state';

import { normalizeOrphanTaskListPlainText, plainTextHasOrphanTaskLines } from './taskListPasteNormalize';

const taskListOrphanPasteKey = new PluginKey('brainfishTaskListOrphanPaste');

/**
 * Rewrites orphan `[ ]` / `[x]` lines to GFM task list markdown on paste, then parses like Milkdown clipboard.
 *
 * Registered by **prepending** to `prosePluginsCtx` so we run before Crepe's bundled clipboard (which only appends).
 *
 * **Why we repeat a few clipboard preconditions:** The first `handlePaste` that calls `preventDefault()` and returns
 * `true` wins — the Milkdown clipboard plugin never runs. So we must `return false` in the same situations where
 * Milkdown would take a *different* path (VS Code → code fence, code block → plain text, empty clipboard). Only
 * then do we optionally intercept pastes that need orphan-task normalization.
 */
export const taskListOrphanPastePlugin: MilkdownPlugin = (ctx) => {
  let prosePlugin: Plugin | undefined;

  return async () => {
    await ctx.wait(SchemaReady);

    prosePlugin = new Plugin({
      key: taskListOrphanPasteKey,
      props: {
        handlePaste: (view, event) => {
          const parser = ctx.get(parserCtx);
          const schema = ctx.get(schemaCtx);
          const editable = view.props.editable?.(view.state);
          const { clipboardData } = event;
          if (!editable || !clipboardData) {
            return false;
          }

          // Same early exits as `@milkdown/plugin-clipboard` — see module JSDoc above.
          if (clipboardData.getData('vscode-editor-data')) {
            return false;
          }

          const currentNode = view.state.selection.$from.node();
          if (currentNode.type.spec.code) {
            return false;
          }

          const text = clipboardData.getData('text/plain');
          if (text.length === 0) {
            return false;
          }

          if (!plainTextHasOrphanTaskLines(text)) {
            return false;
          }

          const normalized = normalizeOrphanTaskListPlainText(text);
          if (normalized === text) {
            return false;
          }

          event.preventDefault();

          const mdSlice = parser(normalized);
          if (!mdSlice || typeof mdSlice === 'string') {
            return false;
          }

          const dom = DOMSerializer.fromSchema(schema).serializeFragment(mdSlice.content);
          const domParser = DOMParser.fromSchema(schema);
          const slice = domParser.parseSlice(dom);
          const node = isTextOnlySlice(slice);
          if (node) {
            view.dispatch(view.state.tr.replaceSelectionWith(node, true));

            return true;
          }

          try {
            view.dispatch(view.state.tr.replaceSelection(slice));

            return true;
          } catch {
            return false;
          }
        },
      },
    });

    ctx.update(prosePluginsCtx, (ps) => [prosePlugin!, ...ps]);

    return () => {
      ctx.update(prosePluginsCtx, (ps) => ps.filter((p) => p !== prosePlugin));
    };
  };
};
